import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cleanupUnverifiedFactors, startChallenge, verifyChallenge } from "@/hooks/use-mfa";

interface Props {
  open: boolean;
  type: "totp" | "phone";
  onOpenChange: (o: boolean) => void;
  onDone: () => void;
}

export function MfaEnrollDialog({ open, type, onOpenChange, onDone }: Props) {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [phone, setPhone] = useState("+44");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFactorId(null); setChallengeId(null); setQr(null); setSecret(null); setCode(""); setBusy(false);
  };

  const begin = async () => {
    setBusy(true);
    try {
      await cleanupUnverifiedFactors();
      if (type === "totp") {
        const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: `Authenticator ${Date.now()}` });
        if (error) throw error;
        setFactorId(data.id);
        setQr(data.totp.qr_code);
        setSecret(data.totp.secret);
        setChallengeId(await startChallenge(data.id));
      } else {
        const clean = phone.replace(/[^\d+]/g, "");
        if (!/^\+\d{8,15}$/.test(clean)) throw new Error("Enter your mobile number with country code, e.g. +447700900123");
        const { data, error } = await supabase.auth.mfa.enroll({ factorType: "phone", phone: clean, friendlyName: `Phone ${Date.now()}` } as any);
        if (error) throw error;
        setFactorId(data.id);
        setChallengeId(await startChallenge(data.id));
        toast.success("Code sent");
      }
    } catch (e: any) {
      toast.error(e.message || "Could not start setup");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!factorId || !challengeId) return;
    setBusy(true);
    try {
      await verifyChallenge(factorId, challengeId, code.trim());
      toast.success("Two-step verification is on");
      reset();
      onDone();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "That code didn't work");
      startChallenge(factorId).then(setChallengeId).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const started = !!factorId;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); cleanupUnverifiedFactors(); } onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{type === "totp" ? "Set up authenticator app" : "Set up text message codes"}</DialogTitle>
          <DialogDescription>
            {type === "totp"
              ? "Use Google Authenticator, Microsoft Authenticator, 1Password or similar."
              : "We'll text a 6-digit code to this number each time you sign in."}
          </DialogDescription>
        </DialogHeader>

        {!started && type === "phone" && (
          <div className="space-y-2">
            <Label>Mobile number</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+447700900123" />
          </div>
        )}

        {!started && (
          <Button onClick={begin} disabled={busy}>{busy ? "Starting..." : type === "totp" ? "Show QR code" : "Send code"}</Button>
        )}

        {started && (
          <div className="space-y-4">
            {qr && (
              <div className="space-y-2 text-center">
                <img src={qr} alt="Scan with your authenticator app" className="mx-auto h-44 w-44 bg-card p-2 rounded" />
                <p className="text-xs text-muted-foreground break-all">Can't scan? Enter this key: <span className="font-mono">{secret}</span></p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Enter the 6-digit code</Label>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="text-center tracking-widest text-lg"
              />
            </div>
            <Button className="w-full" onClick={confirm} disabled={busy || code.length !== 6}>
              {busy ? "Checking..." : "Turn on"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
